import React, { useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Space, 
  Alert,
  Typography
} from 'antd';
import { 
  KeyOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { AdminUser } from './types';

const { Text } = Typography;

interface AdminUserPasswordResetModalProps {
  visible: boolean;
  admin: AdminUser | null;
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: { newPassword: string; confirmPassword: string }) => void;
}

const AdminUserPasswordResetModal: React.FC<AdminUserPasswordResetModalProps> = ({ 
  visible, 
  admin, 
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit
}) => {
  const { t } = useTranslation();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = (values: any) => {
    onSubmit({
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword
    });
  };

  return (
    <Modal
      title={
        <Space>
          <KeyOutlined />
          {t('adminUsers.resetPassword')}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
      destroyOnClose
    >
      {admin && (
        <Alert
          message={t('adminUsers.resetPasswordWarning')}
          description={
            <div>
              <Text strong>{t('adminUsers.resettingPasswordFor')}: </Text>
              <Text>{admin.firstname} {admin.lastname} ({admin.email})</Text>
            </div>
          }
          type="warning"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          label={t('adminUsers.newPassword')}
          name="newPassword"
          rules={[
            { required: true, message: t('adminUsers.pleaseInputNewPassword') },
            { min: 8, message: t('adminUsers.passwordMinLength') },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              message: t('adminUsers.passwordComplexity')
            }
          ]}
          hasFeedback
        >
          <Input.Password 
            placeholder={t('adminUsers.enterNewPassword')}
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          label={t('adminUsers.confirmPassword')}
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('adminUsers.pleaseConfirmPassword') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('adminUsers.passwordsDoNotMatch')));
              },
            }),
          ]}
          hasFeedback
        >
          <Input.Password 
            placeholder={t('adminUsers.confirmNewPassword')}
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
              icon={isSubmitting ? <LoadingOutlined /> : <KeyOutlined />}
              danger
            >
              {isSubmitting ? t('adminUsers.resettingPassword') : t('adminUsers.resetPassword')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdminUserPasswordResetModal;
